import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  showOtp,
  setPhone,
  setLoading,
  setOtpError,
  resendSuccess,
  otpSendSuccess,
  otpVerifySuccess,
  showThanksManual,
  showThanksDigital,
  markSessionExpired,
  restoreSessionTimers,
  markVerifyOtpLimitReached
} from "../slices/otpSlice";
import {
  sendOtp,
  verifyOtp,
  resendOtp,
  manualFallback,
  getSessionStatus,
  submitAvailability
} from "../../services/digitalLeadVerificationApi";
import { OTP_LOADING_KEYS } from "../../templates/strings";

// ── CONSTANTS ───────────────────────────
const SESSION_EXPIRED = "SESSION_EXPIRED";
const COOLDOWN_ACTIVE = "COOLDOWN_ACTIVE";
const TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS";
const COOLDOWN_ERROR_MESSAGE =
  "Bitte warten Sie noch einen Moment vor dem erneuten Senden.";

// ── German error messages matching UI language ───────────────────────────
const ERROR_MESSAGES = {
  INVALID_CODE:
    "Der eingegebene Code ist falsch. Bitte versuchen Sie es erneut.",
  EXPIRED: "Der Code ist abgelaufen. Bitte fordern Sie einen neuen an.",
  TOO_MANY_ATTEMPTS:
    "Zu viele Versuche. Das Verifizierungslimit wurde erreicht. Sie werden nun telefonisch kontaktiert.",
  NO_RECORD: "Sitzung nicht gefunden. Bitte laden Sie die Seite neu.",
  ALREADY_VERIFIED: "Dieser Code wurde bereits verwendet.",
  SESSION_EXPIRED: "Ihre Sitzung ist abgelaufen.",
  DEFAULT: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
};

const _msg = (code) => ERROR_MESSAGES[code] || ERROR_MESSAGES.DEFAULT;

// ── Helpers ───────────────────────────────────────────────────────────────────
const setLoadingState = (dispatch, key, value) =>
  dispatch(setLoading({ key, value }));
const startLoading = (dispatch, key) => setLoadingState(dispatch, key, true);
const stopLoading = (dispatch, key) => setLoadingState(dispatch, key, false);

// ── sendOtpThunk ──────────────────────────────────────────────────────────────
// Called on OtpFlow mount — auto-sends SMS immediately after lead submission
export const sendOtpThunk = createAsyncThunk(
  "sendOtp",
  async ({ leadId, phone }, { dispatch }) => {
    startLoading(dispatch, OTP_LOADING_KEYS.SEND_OTP);
    try {
      const result = await sendOtp(leadId, phone);
      dispatch(otpSendSuccess(result.data)); // stores fallbackAt + resendAvailableAt
    } catch (err) {
      dispatch(setOtpError(_msg(err.response?.data?.data?.reason)));
    } finally {
      stopLoading(dispatch, OTP_LOADING_KEYS.SEND_OTP);
    }
  }
);

// ── verifyOtpThunk ────────────────────────────────────────────────────────────
export const verifyOtpThunk = createAsyncThunk(
  "verifyOtp",
  async ({ leadId, code }, { dispatch }) => {
    startLoading(dispatch, OTP_LOADING_KEYS.VERIFY_OTP);
    try {
      await verifyOtp(leadId, code);
      dispatch(otpVerifySuccess()); // → screen: "availability"
    } catch (err) {
      const errorReason = err.response?.data?.data?.reason;
      if (errorReason === SESSION_EXPIRED) {
        dispatch(markSessionExpired()); // → screen: "thanks-manual"
      } else if (errorReason === TOO_MANY_ATTEMPTS) {
        // User has entered wrong otp 5 times, so we push them to phone queue instead of trapping them on OTP screen
        dispatch(setOtpError(_msg(errorReason)));
        dispatch(markVerifyOtpLimitReached());
        setTimeout(() => {
          dispatch(manualFallbackThunk({ leadId }));
        }, 3000); // wait for state to update before dispatching next thunk
      } else {
        dispatch(setOtpError(_msg(errorReason)));
      }
    } finally {
      stopLoading(dispatch, OTP_LOADING_KEYS.VERIFY_OTP);
    }
  }
);

// ── resendOtpThunk ────────────────────────────────────────────────────────────
// newPhone optional: passed when user corrected number on escalation screen
export const resendOtpThunk = createAsyncThunk(
  "resendOtp",
  async ({ leadId, newPhone = null }, { dispatch }) => {
    startLoading(dispatch, OTP_LOADING_KEYS.RESEND_OTP);
    try {
      const result = await resendOtp(leadId, newPhone);
      dispatch(
        resendSuccess({
          resendAvailableAt: result.data.resendAvailableAt,
          numberCorrectCount: result.data.numberCorrectCount
        })
      );
      dispatch(showOtp()); // always go back to OTP screen after resend
      if (newPhone) {
        dispatch(setPhone({ newPhone }));
      }
    } catch (err) {
      const errRespData = err.response?.data?.data;
      const errorReason = errRespData?.reason;
      if (errorReason === SESSION_EXPIRED) {
        dispatch(markSessionExpired());
      } else if (errorReason === COOLDOWN_ACTIVE) {
        dispatch(
          restoreSessionTimers({
            resendAvailableAt: errRespData?.resendAvailableAt
          })
        );
        dispatch(setOtpError(COOLDOWN_ERROR_MESSAGE));
      } else {
        dispatch(setOtpError(_msg(errorReason)));
      }
    } finally {
      stopLoading(dispatch, OTP_LOADING_KEYS.RESEND_OTP);
    }
  }
);

// ── manualFallbackThunk ───────────────────────────────────────────────────────
// User clicked "Lass dich zurückrufen" — push to phone queue, show standard Greet
export const manualFallbackThunk = createAsyncThunk(
  "manualFallback",
  async ({ leadId }, { dispatch }) => {
    startLoading(dispatch, OTP_LOADING_KEYS.MANUAL_FALLBACK);
    try {
      await manualFallback(leadId);
    } catch (err) {
      console.error("[OTP] manualFallback failed:", err);
      // Still redirect — don't trap user
    } finally {
      stopLoading(dispatch, OTP_LOADING_KEYS.MANUAL_FALLBACK);
      dispatch(showThanksManual());
      // OtpFlow returns null for "thanks-manual"
      // → IndexPage falls through to standard <Greet /> naturally
    }
  }
);

// ── sessionStatusThunk ────────────────────────────────────────────────────────
// Called on mount to re-sync 10-min timer after page refresh
export const sessionStatusThunk = createAsyncThunk(
  "sessionStatus",
  async ({ leadId }, { dispatch }) => {
    startLoading(dispatch, OTP_LOADING_KEYS.SESSION_STATUS);
    try {
      const status = await getSessionStatus(leadId);
      if (status.data.expired) {
        dispatch(markSessionExpired());
      } else if (status.data.fallbackAt) {
        dispatch(restoreSessionTimers({ fallbackAt: status.data.fallbackAt }));
      }
    } catch (err) {
      console.error("[OTP] sessionStatus failed:", err);
    } finally {
      stopLoading(dispatch, OTP_LOADING_KEYS.SESSION_STATUS);
    }
  }
);

// ── submitAvailabilityThunk ───────────────────────────────────────────────────
// One-click: user selects slot, update lead's optimalAvailability and marked as qualifed
export const submitAvailabilityThunk = createAsyncThunk(
  "submitAvailability",
  async ({ leadId, optimalAvailability }, { dispatch }) => {
    startLoading(dispatch, OTP_LOADING_KEYS.SUBMIT_AVAILABILITY);
    try {
      await submitAvailability(leadId, optimalAvailability);
      dispatch(showThanksDigital());
    } catch (err) {
      const errorReason = err.response?.data?.data?.reason;
      if (errorReason === SESSION_EXPIRED) {
        dispatch(markSessionExpired());
      } else {
        console.error("[OTP] submitAvailability failed:", err);
        dispatch(showThanksDigital()); // still advance — user already clicked
      }
    } finally {
      stopLoading(dispatch, OTP_LOADING_KEYS.SUBMIT_AVAILABILITY);
    }
  }
);
